/**
 * useTutorSession Hook
 *
 * Manages the Addison Tutor voice session:
 * - Fetches signed URL + session token from voice-tutor-signed-url
 * - Starts ElevenLabs conversation with client tools
 * - Client tools update visual state with security validation
 * - Integrates speaker ID via sendContextualUpdate
 *
 * See spec: docs/superpowers/specs/2026-04-12-addison-tutor-design.md
 */

import { useState, useCallback, useRef, useEffect } from 'react';
import * as Sentry from '@sentry/react';
import { Conversation } from '@elevenlabs/client';
import { supabase } from '@/lib/supabase/client';
import { STANDARD_COLORS } from '@/lib/turn-color';
import { CELEBRATION_WHITELIST, PHONEME_COLORS } from '@/components/tutor/constants';
import type { VisualState } from '@/components/tutor/TutorDisplay';

// Supabase Storage public URL for phoneme audio
const PHONEME_AUDIO_BASE = `${import.meta.env.VITE_SUPABASE_URL}/storage/v1/object/public/phoneme-audio`;

// =============================================================================
// Types
// =============================================================================

export type TutorSessionStatus = 'idle' | 'connecting' | 'connected' | 'disconnected' | 'error';

export interface TutorSessionData {
  signedUrl: string;
  sessionToken: string;
  dynamicVariables: Record<string, string>;
  currentWords: string[];
  teachingSteps: Record<string, unknown[]>;
  householdMembers: Array<{ name: string; role: string }>;
}

interface UseTutorSessionOptions {
  childId: string;
  onSpeakerChange?: (name: string, role: string) => void;
  onMessage?: (msg: { role: string; text: string }) => void;
}

// =============================================================================
// Hook
// =============================================================================

export function useTutorSession({ childId, onSpeakerChange, onMessage }: UseTutorSessionOptions) {
  const [status, setStatus] = useState<TutorSessionStatus>('idle');
  const [visualState, setVisualState] = useState<VisualState>({ type: 'idle' });
  const [transcript, setTranscript] = useState<Array<{ role: string; text: string }>>([]);
  const [sessionData, setSessionData] = useState<TutorSessionData | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const conversationRef = useRef<Awaited<ReturnType<typeof Conversation.startSession>> | null>(null);
  const currentWordsRef = useRef<string[]>([]);
  const lastSpeechTimestampRef = useRef<number>(Date.now());
  const startingRef = useRef(false); // Mutex to prevent double-start race condition
  const phonemeAudioRef = useRef<HTMLAudioElement | null>(null);
  const onSpeakerChangeRef = useRef(onSpeakerChange);
  const onMessageRef = useRef(onMessage);
  onSpeakerChangeRef.current = onSpeakerChange;
  onMessageRef.current = onMessage;

  // ---------------------------------------------------------------------------
  // Client tools with security validation (spec §9: H-4, L-7)
  // ---------------------------------------------------------------------------

  // Shared AudioContext for phoneme playback with volume boost
  const audioCtxRef = useRef<AudioContext | null>(null);

  // Track whether phoneme playback is active (for safety restore in onModeChange)
  const phonemePlayingRef = useRef(false);

  // Helper: duck agent TTS volume, play phoneme WAV with boost, restore volume.
  // Uses volume ducking (0.05) instead of full mute (0) to prevent audio buffer
  // accumulation — at volume 0, TTS buffers silently and flushes on unmute.
  // At 0.05, the audio pipeline stays live (inaudible under 4x phoneme boost).
  const playPhonemeWithDuck = async (color: string): Promise<string> => {
    if (!PHONEME_COLORS.includes(color as any)) return '';
    phonemePlayingRef.current = true;
    try {
      // Stop any previously playing phoneme
      phonemeAudioRef.current?.pause();
      phonemeAudioRef.current = null;
      // Duck (not mute) agent TTS — 5% volume prevents silent buffer accumulation
      conversationRef.current?.setVolume({ volume: 0.05 });

      const url = `${PHONEME_AUDIO_BASE}/${color}-phoneme.wav`;

      // Use Web Audio API for volume boost (WAV files are ~15% peak, need ~4x boost)
      if (!audioCtxRef.current) {
        audioCtxRef.current = new AudioContext();
      }
      const ctx = audioCtxRef.current;
      if (ctx.state === 'suspended') await ctx.resume();

      const response = await fetch(url);
      const arrayBuffer = await response.arrayBuffer();
      const audioBuffer = await ctx.decodeAudioData(arrayBuffer);

      const source = ctx.createBufferSource();
      const gainNode = ctx.createGain();
      gainNode.gain.value = 4.0; // Boost to match ElevenLabs TTS volume
      source.buffer = audioBuffer;
      source.connect(gainNode).connect(ctx.destination);

      // Wait for playback to finish
      await new Promise<void>(resolve => {
        source.onended = () => resolve();
        source.start(0);
      });

      // Brief pause after phoneme, then restore — no buffer flush since we ducked, not muted
      await new Promise(r => setTimeout(r, 300));
      conversationRef.current?.setVolume({ volume: 1 });
      phonemePlayingRef.current = false;
      return ` The ${color} phoneme sound was played. WAIT 3 seconds before speaking.`;
    } catch {
      // Restore volume even on failure
      conversationRef.current?.setVolume({ volume: 1 });
      phonemePlayingRef.current = false;
      return ' Audio playback failed - please voice the phoneme sound yourself as a fallback.';
    }
  };

  const buildClientTools = useCallback(() => ({
    show_consonant: async ({ letter }: { letter: string }) => {
      if (!/^[a-zA-Z]{1,4}$/.test(letter)) return 'Invalid letter';
      setVisualState({ type: 'consonant', letter });
      return 'Displayed';
    },
    show_color: async ({ color }: { color: string }) => {
      if (!STANDARD_COLORS.some(c => c.color_name === color)) return 'Invalid color';
      setVisualState({ type: 'color', color });
      const phonemeResult = await playPhonemeWithDuck(color);
      return 'Displayed.' + phonemeResult;
    },
    show_letter_on_color: async ({ letter, color }: { letter: string; color: string }) => {
      if (!/^[a-zA-Z]{1,4}$/.test(letter)) return 'Invalid letter';
      if (!STANDARD_COLORS.some(c => c.color_name === color)) return 'Invalid color';
      setVisualState({ type: 'letter_on_color', letter, color });
      const phonemeResult = await playPhonemeWithDuck(color);
      return 'Displayed.' + phonemeResult;
    },
    show_word: async ({ word, turn_code }: { word: string; turn_code: string }) => {
      // Fail-closed: reject if word list is populated and word not in it
      if (!currentWordsRef.current.includes(word)) {
        // Allow through only if word list hasn't loaded yet AND word looks reasonable
        if (currentWordsRef.current.length > 0 || word.length > 30) return 'Invalid word';
      }
      setVisualState({ type: 'word', word, turnCode: turn_code });
      return 'Displayed';
    },
    show_celebration: async ({ message }: { message: string }) => {
      const safe = CELEBRATION_WHITELIST.includes(message) ? message : 'Great job!';
      setVisualState({ type: 'celebration', message: safe });
      return 'Displayed';
    },
    clear_display: async () => {
      setVisualState({ type: 'idle' });
      return 'Cleared';
    },
  }), []);

  // ---------------------------------------------------------------------------
  // Start Session
  // ---------------------------------------------------------------------------

  const startSession = useCallback(async () => {
    // Ref-based mutex prevents double-start from rapid clicks
    if (conversationRef.current || startingRef.current) return;
    startingRef.current = true;

    setStatus('connecting');
    setErrorMessage(null);

    try {
      // 1. Get auth token (getSession reads local storage; edge function validates JWT server-side)
      const { data: { session: authSession } } = await supabase.auth.getSession();
      if (!authSession?.access_token) {
        startingRef.current = false;
        setStatus('error');
        setErrorMessage('Not authenticated');
        return;
      }

      // 2. Fetch signed URL + session token from edge function
      const res = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/voice-tutor-signed-url`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${authSession.access_token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ childId }),
        }
      );

      const data = await res.json();

      if (!res.ok || data.error) {
        startingRef.current = false;
        setStatus('error');
        setErrorMessage(data.error || 'Failed to start session');
        return;
      }

      setSessionData(data);
      currentWordsRef.current = data.currentWords || [];

      // 3. Request mic permission (warm-up before SDK takes it)
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        stream.getTracks().forEach(t => t.stop()); // release — SDK will acquire its own
      } catch {
        startingRef.current = false;
        setStatus('error');
        setErrorMessage('Microphone access is required');
        return;
      }

      // 4. Start ElevenLabs conversation
      // Dynamic variables resolve {{ placeholders }} in the system prompt.
      // firstMessage is overridden with the child's name pre-resolved
      // because ElevenLabs does NOT substitute dynamic variables in first_message TTS.
      // The agent's configured first_message is a generic fallback; we override it here.
      const childName = data.dynamicVariables?.child_name || 'Learner';
      const personalizedGreeting = `Hi ${childName}! It's Addison! Let's practice some sounds together!`;
      const conversation = await Conversation.startSession({
        signedUrl: data.signedUrl,
        dynamicVariables: data.dynamicVariables,
        overrides: {
          agent: {
            firstMessage: personalizedGreeting,
            language: 'en',
          },
        },
        clientTools: buildClientTools(),
        onConnect: () => {
          setStatus('connected');
          setErrorMessage(null);
          lastSpeechTimestampRef.current = Date.now();
          // Confirm child as initial speaker via contextual update.
          // Use conversationRef (set after startSession resolves) via setTimeout
          // because `conversation` local var isn't assigned yet during onConnect.
          setTimeout(() => {
            conversationRef.current?.sendContextualUpdate(
              `Session started. Speaking with: ${childName} (child)`
            );
          }, 0);
          onSpeakerChangeRef.current?.(childName, 'child');
        },
        onDisconnect: () => {
          setStatus('disconnected');
          setVisualState({ type: 'idle' });
          conversationRef.current = null;
        },
        onMessage: (message: { source: string; message: string }) => {
          lastSpeechTimestampRef.current = Date.now();
          const entry = {
            role: message.source === 'user' ? 'user' : 'agent',
            text: message.message,
          };
          setTranscript(prev => [...prev, entry]);
          onMessageRef.current?.(entry);
        },
        onError: (error: unknown) => {
          Sentry.captureException(error, { tags: { module: 'tutor', event: 'connection_error' } });
          setStatus('error');
          setErrorMessage('Connection error');
        },
        onModeChange: (mode: { mode: string }) => {
          if (mode.mode === 'listening') {
            lastSpeechTimestampRef.current = Date.now();
            // Safety: if phoneme playback left volume ducked, restore it
            if (phonemePlayingRef.current) {
              conversationRef.current?.setVolume({ volume: 1 });
              phonemePlayingRef.current = false;
            }
          }
        },
      });

      conversationRef.current = conversation;
      startingRef.current = false;
    } catch (err) {
      Sentry.captureException(err, { tags: { module: 'tutor', event: 'start_failed' } });
      startingRef.current = false;
      setStatus('error');
      setErrorMessage('Failed to start session');
    }
  }, [childId, buildClientTools]);

  // ---------------------------------------------------------------------------
  // End Session
  // ---------------------------------------------------------------------------

  const endSession = useCallback(async () => {
    try {
      await conversationRef.current?.endSession();
    } catch {
      // Ignore cleanup errors
    }
    conversationRef.current = null;
    phonemeAudioRef.current?.pause();
    phonemeAudioRef.current = null;
    setVisualState({ type: 'idle' });
    setStatus('disconnected');
  }, []);

  // ---------------------------------------------------------------------------
  // Speaker Update (called by sherpa-onnx or manual picker)
  // ---------------------------------------------------------------------------

  const updateSpeaker = useCallback((name: string, role: string) => {
    conversationRef.current?.sendContextualUpdate(
      `Speaker changed to: ${name} (${role})`
    );
    lastSpeechTimestampRef.current = Date.now();
    onSpeakerChangeRef.current?.(name, role);
  }, []);

  // Cleanup on unmount — end session to release resources
  useEffect(() => {
    return () => {
      if (conversationRef.current) {
        conversationRef.current.endSession().catch(() => {});
        conversationRef.current = null;
      }
      phonemeAudioRef.current?.pause();
      phonemeAudioRef.current = null;
    };
  }, []);

  return {
    status,
    visualState,
    transcript,
    sessionData,
    errorMessage,
    lastSpeechTimestampRef,
    startSession,
    endSession,
    updateSpeaker,
  };
}
